import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';
import { Layout } from '../../components/Layout';
import { TaskEditModal } from '../../components/TaskEditModal';
import { TimeEntryModal } from '../../components/TimeEntryModal';
import { TaskCreateForm } from '../../components/TaskCreateForm';
import { tasksApi, UpdateTaskRequest, CreateTaskRequest } from '../../services/tasks';
import { casesApi } from '../../services/cases';
import { timeApi, CreateTimeEntryRequest } from '../../services/time';
import { Task, Case, TaskPriority, TaskStatus } from '../../types';
import { formatLocalDate } from '../../utils/date';
import {
  TaskWithCase,
  combineFilters,
  TaskFilterConfig,
} from './taskUtils';

/**
 * Generates navigation URL for a case detail page.
 * 
 * Property 10: Student name click generates correct navigation URL
 * Validates: Requirements 6.1
 */
export function generateCaseNavigationUrl(caseId: string): string {
  return `/cases/${caseId}`;
}

/**
 * Toggles task status between completed and pending.
 * 
 * Property 7: Status toggle updates task status correctly
 * Validates: Requirements 4.1, 4.5
 */
export function toggleTaskStatus(currentStatus: TaskStatus): TaskStatus {
  if (currentStatus === 'completed') {
    return 'pending';
  }
  return 'completed';
}

const columnHelper = createColumnHelper<TaskWithCase>();

// Priority pill colors
const priorityColors: Record<TaskPriority, { bg: string; text: string }> = {
  low: { bg: '#e3f2fd', text: '#1565c0' },
  medium: { bg: '#fff3e0', text: '#e65100' },
  high: { bg: '#ffebee', text: '#c62828' },
};

/**
 * TaskListPage - Main page component for the task table view.
 * Displays all tasks for an advocate in a sortable, filterable table.
 * 
 * Requirements: 1.1, 1.2, 9.1
 */
export function TaskListPage() {
  const navigate = useNavigate();
  
  // Data state
  const [tasks, setTasks] = useState<Task[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter state
  const [showCompleted, setShowCompleted] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority[]>([]);
  const [studentFilter, setStudentFilter] = useState<string[]>([]);
  
  // Modal state
  const [editingTask, setEditingTask] = useState<TaskWithCase | null>(null);
  const [completingTask, setCompletingTask] = useState<TaskWithCase | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Table sorting state
  const [sorting, setSorting] = useState<SortingState>([]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [tasksResponse, casesResponse] = await Promise.all([
        tasksApi.list(),
        casesApi.list(),
      ]);
      
      setTasks(tasksResponse.tasks);
      setCases(casesResponse.cases);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Create TaskWithCase by joining tasks with case data
  const tasksWithCase: TaskWithCase[] = useMemo(() => {
    const caseMap = new Map(cases.map(c => [c.caseId, c]));
    return tasks.map(task => {
      const caseData = caseMap.get(task.caseId);
      const studentName = caseData 
        ? `${caseData.childFirstName} ${caseData.childLastName}`
        : 'Unknown';
      return { ...task, studentName };
    });
  }, [tasks, cases]);

  // Apply filters
  const filteredTasks = useMemo(() => {
    const config: TaskFilterConfig = {
      showCompleted,
      priorities: priorityFilter,
      caseIds: studentFilter,
    };
    return combineFilters(tasksWithCase, config);
  }, [tasksWithCase, showCompleted, priorityFilter, studentFilter]);

  // Get unique students for filter dropdown
  const studentOptions = useMemo(() => {
    const uniqueStudents = new Map<string, { caseId: string; name: string }>();
    tasksWithCase.forEach(task => {
      if (!uniqueStudents.has(task.caseId)) {
        uniqueStudents.set(task.caseId, {
          caseId: task.caseId,
          name: task.studentName,
        });
      }
    });
    return Array.from(uniqueStudents.values()).sort((a, b) => 
      a.name.localeCompare(b.name)
    );
  }, [tasksWithCase]);


  // Handle task completion toggle
  const handleToggleComplete = async (task: TaskWithCase) => {
    const newStatus = toggleTaskStatus(task.status);
    
    if (newStatus === 'completed') {
      // Show time entry modal when completing
      setCompletingTask(task);
    } else {
      // Directly uncomplete without modal
      try {
        await tasksApi.update(task.taskId, { status: newStatus });
        await loadData();
      } catch (err) {
        console.error('Failed to update task:', err);
        alert('Failed to update task status. Please try again.');
      }
    }
  };

  // Handle time entry submission after completing task
  const handleTimeEntrySubmit = async (timeEntry: CreateTimeEntryRequest) => {
    if (!completingTask) return;
    
    try {
      // First update task status
      await tasksApi.update(completingTask.taskId, { status: 'completed' });
      // Then create time entry
      await timeApi.create(timeEntry);
      setCompletingTask(null);
      await loadData();
    } catch (err) {
      console.error('Failed to complete task with time entry:', err);
      alert('Failed to complete task. Please try again.');
    }
  };

  // Handle skipping time entry
  const handleTimeEntrySkip = async () => {
    if (!completingTask) return;
    
    try {
      await tasksApi.update(completingTask.taskId, { status: 'completed' });
      setCompletingTask(null);
      await loadData();
    } catch (err) {
      console.error('Failed to complete task:', err);
      alert('Failed to complete task. Please try again.');
    }
  };

  // Handle canceling task completion - abandons the operation without any changes
  const handleTimeEntryCancel = () => {
    setCompletingTask(null);
  };

  // Handle task edit save
  const handleEditSave = async (taskId: string, updates: UpdateTaskRequest) => {
    await tasksApi.update(taskId, updates);
    setEditingTask(null);
    await loadData();
  };

  // Handle task creation
  const handleCreateTask = async (request: CreateTaskRequest) => {
    await tasksApi.create(request);
    setShowCreateForm(false);
    await loadData();
  };

  // Handle student name click - navigate to case detail
  const handleStudentClick = (caseId: string) => {
    const url = generateCaseNavigationUrl(caseId);
    navigate(url);
  };

  // Table columns definition
  const columns = useMemo(() => [
    // Completion checkbox column (unlabeled)
    columnHelper.display({
      id: 'completed',
      header: '',
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.original.status === 'completed'}
          onChange={() => handleToggleComplete(row.original)}
          style={{ cursor: 'pointer', width: '18px', height: '18px' }}
        />
      ),
      size: 40,
    }),
    // Priority pill column (unlabeled, clickable)
    columnHelper.display({
      id: 'priority',
      header: '',
      cell: ({ row }) => {
        const priority = row.original.priority;
        const colors = priorityColors[priority];
        return (
          <span
            onClick={() => setEditingTask(row.original)}
            style={{
              display: 'inline-block',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500,
              backgroundColor: colors.bg,
              color: colors.text,
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {priority}
          </span>
        );
      },
      size: 80,
    }),
    // Task title column (clickable)
    columnHelper.accessor('title', {
      header: 'Task',
      cell: ({ row }) => (
        <span
          onClick={() => setEditingTask(row.original)}
          style={{
            cursor: 'pointer',
            color: '#1976d2',
            textDecoration: row.original.status === 'completed' ? 'line-through' : 'none',
          }}
        >
          {row.original.title}
        </span>
      ),
    }),
    // Student name column (clickable link)
    columnHelper.accessor('studentName', {
      header: 'Student',
      cell: ({ row }) => (
        <span
          onClick={() => handleStudentClick(row.original.caseId)}
          style={{
            cursor: 'pointer',
            color: '#1976d2',
            textDecoration: 'underline',
          }}
        >
          {row.original.studentName}
        </span>
      ),
      sortingFn: 'alphanumeric',
    }),
    // Due date column
    columnHelper.accessor('dueDate', {
      header: 'Due Date',
      cell: ({ row }) => {
        const dueDate = row.original.dueDate;
        if (!dueDate) return <span style={{ color: '#999' }}>—</span>;
        
        const today = new Date().toISOString().split('T')[0];
        const isOverdue = dueDate < today && row.original.status !== 'completed';
        
        return (
          <span style={{ color: isOverdue ? '#c62828' : 'inherit' }}>
            {formatLocalDate(dueDate)}
          </span>
        );
      },
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.dueDate;
        const b = rowB.original.dueDate;
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return a.localeCompare(b);
      },
    }),
  ], []);

  // Create table instance
  const table = useReactTable({
    data: filteredTasks,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });


  // Clear all filters
  const handleClearFilters = () => {
    setPriorityFilter([]);
    setStudentFilter([]);
    setShowCompleted(false);
  };

  const hasActiveFilters = priorityFilter.length > 0 || studentFilter.length > 0 || showCompleted;

  return (
    <Layout>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0 }}>Tasks</h1>
          <p style={{ color: '#666', marginTop: '8px' }}>
            Manage all your tasks across cases
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          style={{
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          + Add Task
        </button>
      </div>

      {/* Filter Controls */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        {/* Completed Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '14px', color: '#666' }}>Show Completed</label>
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              border: 'none',
              backgroundColor: showCompleted ? '#1976d2' : '#ccc',
              cursor: 'pointer',
              position: 'relative',
              transition: 'background-color 0.2s',
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: '2px',
                left: showCompleted ? '22px' : '2px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: 'white',
                transition: 'left 0.2s',
              }}
            />
          </button>
        </div>

        {/* Priority Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '14px', color: '#666' }}>Priority</label>
          <select
            multiple
            value={priorityFilter}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, opt => opt.value as TaskPriority);
              setPriorityFilter(selected);
            }}
            style={{
              padding: '6px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '120px',
            }}
          >
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        {/* Student Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '14px', color: '#666' }}>Student</label>
          <select
            value={studentFilter[0] || ''}
            onChange={(e) => {
              setStudentFilter(e.target.value ? [e.target.value] : []);
            }}
            style={{
              padding: '6px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '150px',
            }}
          >
            <option value="">All Students</option>
            {studentOptions.map(opt => (
              <option key={opt.caseId} value={opt.caseId}>
                {opt.name}
              </option>
            ))}
          </select>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#666',
            }}
          >
            Clear Filters
          </button>
        )}

        {/* Task count */}
        <div style={{ marginLeft: 'auto', fontSize: '14px', color: '#666' }}>
          {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div style={{
          backgroundColor: '#ffebee',
          color: '#c62828',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>{error}</span>
          <button
            onClick={loadData}
            style={{
              backgroundColor: '#c62828',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '48px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <p style={{ color: '#666' }}>Loading tasks...</p>
        </div>
      ) : (
        /* Table */
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id} style={{ borderBottom: '2px solid #e0e0e0' }}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                      style={{
                        textAlign: 'left',
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#666',
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        userSelect: 'none',
                        width: header.getSize() !== 150 ? header.getSize() : undefined,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() && (
                          <span>{header.column.getIsSorted() === 'asc' ? ' ↑' : ' ↓'}</span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={{ padding: '48px', textAlign: 'center', color: '#666' }}>
                    {hasActiveFilters ? 'No tasks match your filters.' : 'No tasks yet. Create your first task!'}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: row.original.status === 'completed' ? '#fafafa' : 'transparent',
                    }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} style={{ padding: '12px 16px', fontSize: '14px' }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {editingTask && (
        <TaskEditModal
          task={editingTask}
          onSave={handleEditSave}
          onClose={() => setEditingTask(null)}
        />
      )}

      {completingTask && (
        <TimeEntryModal
          task={completingTask}
          onSubmit={handleTimeEntrySubmit}
          onSkip={handleTimeEntrySkip}
          onCancel={handleTimeEntryCancel}
        />
      )}

      {showCreateForm && (
        <TaskCreateForm
          cases={cases}
          onSubmit={handleCreateTask}
          onCancel={() => setShowCreateForm(false)}
        />
      )}
    </Layout>
  );
}
